/**
 * Insight detection algorithms
 * Each detector analyzes specific patterns and generates insights
 */

import { ProfitInsight, InsightType, InsightPriority, EstimatedImpact } from '../types/insights';
import { DailyProfitSummary, HourlyProfitBreakdown, AssetBreakdownSummary } from '../api/profit';
import { groupByDayOfWeek, groupByHour, groupByAsset, DayOfWeekGroup, HourOfDayGroup, AssetGroup } from '../utils/insightGroupingUtils';
import { calculateMean, calculateMovingAverage, calculateTrendSlope } from '../utils/statisticsUtils';

/**
 * Minimum data requirements for day of week analysis
 */
const MIN_WEEKS_REQUIRED = 4;
const MIN_DAYS_PER_DAY_OF_WEEK = 3;

/**
 * Thresholds for detection
 */
const HIGH_PERFORMANCE_THRESHOLD = 0.20; // 20% above average
const LOW_PERFORMANCE_THRESHOLD = -0.20; // 20% below average
const LOW_WIN_RATE_THRESHOLD = 0.60; // 60% win rate minimum
const HIGH_WIN_RATE_THRESHOLD = 0.75; // 75% win rate for opportunity

/**
 * Analyze day of week performance patterns
 * Detects which days consistently outperform or underperform
 * 
 * @param dailyData - Array of daily profit summaries
 * @returns Array of insights about day of week patterns
 */
export function analyzeDayOfWeekPerformance(dailyData: DailyProfitSummary[]): ProfitInsight[] {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }

  // Check minimum data requirement (at least 4 weeks of data)
  const totalDays = dailyData.length;
  if (totalDays < MIN_WEEKS_REQUIRED * 7) {
    return [];
  }

  // Group data by day of week
  const dayGroups = groupByDayOfWeek(dailyData);
  
  // Filter out days with insufficient data
  const validDays = dayGroups.filter(day => day.totalDays >= MIN_DAYS_PER_DAY_OF_WEEK);
  
  if (validDays.length === 0) {
    return [];
  }

  // Calculate average profit per day across all days
  const avgProfitPerDayAllDays = calculateMean(validDays.map(d => d.avgProfitPerDay));

  const insights: ProfitInsight[] = [];

  validDays.forEach(dayGroup => {
    const performanceRatio = avgProfitPerDayAllDays !== 0
      ? (dayGroup.avgProfitPerDay - avgProfitPerDayAllDays) / Math.abs(avgProfitPerDayAllDays)
      : 0;

    // High-performing day with good win rate
    if (performanceRatio > HIGH_PERFORMANCE_THRESHOLD && dayGroup.winRate > HIGH_WIN_RATE_THRESHOLD) {
      const weeklyImpact = dayGroup.avgProfitPerDay * 4.33; // ~4.33 weeks per month
      
      insights.push({
        id: `day-of-week-opportunity-${dayGroup.dayOfWeek}`,
        type: 'opportunity',
        priority: weeklyImpact > 200 ? 'high' : 'medium',
        title: `${dayGroup.dayName} is a Strong Performance Day`,
        description: `${dayGroup.dayName} shows exceptional results with ${dayGroup.winRate.toFixed(1)}% win rate and average profit of $${dayGroup.avgProfitPerDay.toFixed(2)} per day, which is ${Math.abs(performanceRatio * 100).toFixed(0)}% above your weekly average. Consider increasing your trading activity on ${dayGroup.dayName}s to capitalize on this pattern.`,
        estimatedImpact: {
          amount: weeklyImpact,
          period: 'week',
          direction: 'gain',
        },
        confidenceScore: Math.min(dayGroup.totalDays / 12, 1), // Confidence based on data points
        metadata: {
          source: 'day-of-week-analyzer',
          dayOfWeek: dayGroup.dayOfWeek,
          dayName: dayGroup.dayName,
          winRate: dayGroup.winRate,
          avgProfitPerDay: dayGroup.avgProfitPerDay,
          totalDays: dayGroup.totalDays,
        },
      });
    }

    // Low-performing day or low win rate
    if (performanceRatio < LOW_PERFORMANCE_THRESHOLD || dayGroup.winRate < LOW_WIN_RATE_THRESHOLD) {
      const weeklyRisk = Math.abs(dayGroup.avgProfitPerDay) * 4.33;
      
      insights.push({
        id: `day-of-week-warning-${dayGroup.dayOfWeek}`,
        type: 'warning',
        priority: dayGroup.winRate < 0.50 ? 'high' : 'medium',
        title: `${dayGroup.dayName} Shows Weak Performance`,
        description: `${dayGroup.dayName} has concerning results with ${dayGroup.winRate.toFixed(1)}% win rate and average profit of $${dayGroup.avgProfitPerDay.toFixed(2)} per day. This is ${Math.abs(performanceRatio * 100).toFixed(0)}% below your weekly average. Consider reducing activity or avoiding trading on ${dayGroup.dayName}s until this pattern improves.`,
        estimatedImpact: {
          amount: weeklyRisk,
          period: 'week',
          direction: 'risk',
        },
        confidenceScore: Math.min(dayGroup.totalDays / 12, 1),
        metadata: {
          source: 'day-of-week-analyzer',
          dayOfWeek: dayGroup.dayOfWeek,
          dayName: dayGroup.dayName,
          winRate: dayGroup.winRate,
          avgProfitPerDay: dayGroup.avgProfitPerDay,
          totalDays: dayGroup.totalDays,
        },
      });
    }
  });

  return insights;
}

/**
 * Analyze hour of day performance patterns
 * Detects which hours consistently outperform
 * 
 * @param hourlyData - Array of hourly profit breakdowns
 * @returns Array of insights about hour of day patterns
 */
export function analyzeHourOfDayPerformance(hourlyData: HourlyProfitBreakdown[]): ProfitInsight[] {
  if (!hourlyData || hourlyData.length === 0) {
    return [];
  }

  // Require minimum 14 days of data
  const uniqueDates = new Set(hourlyData.map(h => h.trade_date));
  if (uniqueDates.size < 14) {
    return [];
  }

  // Group by hour
  const hourGroups = groupByHour(hourlyData);
  
  // Filter to hours with actual trading activity
  const activeHours = hourGroups.filter(h => h.tradeCount > 0);
  
  if (activeHours.length === 0) {
    return [];
  }

  // Calculate average profit per occurrence across all hours
  const avgProfitPerOccurrence = calculateMean(activeHours.map(h => h.avgProfitPerOccurrence));

  const insights: ProfitInsight[] = [];

  // Find top performing hours (>30% above average)
  const HOUR_HIGH_PERFORMANCE_THRESHOLD = 0.30;
  
  activeHours.forEach(hourGroup => {
    if (hourGroup.occurrenceCount < 7) {
      // Skip hours with insufficient data
      return;
    }

    const performanceRatio = avgProfitPerOccurrence !== 0
      ? (hourGroup.avgProfitPerOccurrence - avgProfitPerOccurrence) / Math.abs(avgProfitPerOccurrence)
      : 0;

    if (performanceRatio > HOUR_HIGH_PERFORMANCE_THRESHOLD) {
      // Estimate daily impact (assume trading this hour 5 days/week)
      const weeklyImpact = hourGroup.avgProfitPerOccurrence * 5;
      
      const formatHour = (hour: number) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}${period}`;
      };

      insights.push({
        id: `hour-of-day-opportunity-${hourGroup.hour}`,
        type: 'opportunity',
        priority: weeklyImpact > 150 ? 'high' : 'medium',
        title: `${formatHour(hourGroup.hour)} is a Peak Performance Hour`,
        description: `Trading at ${formatHour(hourGroup.hour)} shows exceptional results with average profit of $${hourGroup.avgProfitPerOccurrence.toFixed(2)} per occurrence, which is ${Math.abs(performanceRatio * 100).toFixed(0)}% above your hourly average. Consider focusing more trading activity during this time window.`,
        estimatedImpact: {
          amount: weeklyImpact,
          period: 'week',
          direction: 'gain',
        },
        confidenceScore: Math.min(hourGroup.occurrenceCount / 20, 1),
        metadata: {
          source: 'hour-of-day-analyzer',
          hour: hourGroup.hour,
          avgProfitPerOccurrence: hourGroup.avgProfitPerOccurrence,
          occurrenceCount: hourGroup.occurrenceCount,
        },
      });
    }
  });

  return insights;
}

/**
 * Analyze asset performance
 * Detects which assets deliver superior profit per trade
 * 
 * @param assetBreakdowns - Array of asset breakdown summaries from API
 * @returns Array of insights about asset performance
 */
export function analyzeAssetPerformance(assetBreakdowns: AssetBreakdownSummary[]): ProfitInsight[] {
  if (!assetBreakdowns || assetBreakdowns.length === 0) {
    return [];
  }

  // Filter to assets with sufficient trade count (minimum 20 trades)
  const MIN_TRADES_PER_ASSET = 20;
  const validAssets = assetBreakdowns.filter(a => a.trade_count >= MIN_TRADES_PER_ASSET);
  
  if (validAssets.length === 0) {
    return [];
  }

  // Calculate portfolio average profit per trade
  const portfolioAvgProfitPerTrade = calculateMean(validAssets.map(a => a.avg_profit_per_trade));

  const insights: ProfitInsight[] = [];
  const ASSET_HIGH_PERFORMANCE_THRESHOLD = 0.50; // 50% above portfolio average

  validAssets.forEach(asset => {
    const performanceRatio = portfolioAvgProfitPerTrade !== 0
      ? (asset.avg_profit_per_trade - portfolioAvgProfitPerTrade) / Math.abs(portfolioAvgProfitPerTrade)
      : 0;

    if (performanceRatio > ASSET_HIGH_PERFORMANCE_THRESHOLD) {
      // Estimate monthly impact if allocation increased
      const currentMonthlyFromAsset = asset.net_profit; // Already for the period
      const potentialGainMultiplier = 0.20; // Conservative 20% increase assumption
      const monthlyImpact = currentMonthlyFromAsset * potentialGainMultiplier;

      insights.push({
        id: `asset-performance-opportunity-${asset.product_id}`,
        type: 'opportunity',
        priority: monthlyImpact > 300 ? 'high' : 'medium',
        title: `${asset.product_id} Shows Superior Performance`,
        description: `${asset.product_id} delivers exceptional results with $${asset.avg_profit_per_trade.toFixed(2)} profit per trade, which is ${Math.abs(performanceRatio * 100).toFixed(0)}% above your portfolio average. This asset accounts for ${asset.percent_of_total.toFixed(1)}% of total profit from ${asset.trade_count} trades. Consider increasing allocation to this high-performing asset.`,
        estimatedImpact: {
          amount: monthlyImpact,
          period: 'month',
          direction: 'gain',
        },
        confidenceScore: Math.min(asset.trade_count / 100, 1),
        metadata: {
          source: 'asset-performance-analyzer',
          productId: asset.product_id,
          avgProfitPerTrade: asset.avg_profit_per_trade,
          tradeCount: asset.trade_count,
          percentOfTotal: asset.percent_of_total,
        },
      });
    }
  });

  return insights;
}

/**
 * Analyze fee ratio and alert if exceeding target
 * Target fee ratio is typically 5% or less
 * 
 * @param dailyData - Array of daily profit summaries
 * @returns Array of insights about fee efficiency
 */
export function analyzeFeeRatio(dailyData: DailyProfitSummary[]): ProfitInsight[] {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }

  // Calculate total fees and gross profit
  const totalFees = dailyData.reduce((sum, day) => sum + (day.fees_paid || 0), 0);
  const totalGrossProfit = dailyData.reduce((sum, day) => sum + day.gross_profit, 0);

  if (totalGrossProfit <= 0) {
    return [];
  }

  // Calculate fee ratio (fees / gross profit * 100)
  const feeRatio = (totalFees / totalGrossProfit) * 100;
  const TARGET_FEE_RATIO = 5.0;

  const insights: ProfitInsight[] = [];

  if (feeRatio > TARGET_FEE_RATIO) {
    // Calculate potential savings if reduced to target
    const targetFees = (totalGrossProfit * TARGET_FEE_RATIO) / 100;
    const potentialSavings = totalFees - targetFees;
    
    // Calculate monthly savings estimate
    const daysInPeriod = dailyData.length;
    const monthlySavings = (potentialSavings / daysInPeriod) * 30;

    insights.push({
      id: 'fee-ratio-warning',
      type: 'warning',
      priority: feeRatio > 7 ? 'high' : 'medium',
      title: 'Fee Ratio Above Target',
      description: `Your current fee ratio is ${feeRatio.toFixed(2)}%, which exceeds the ${TARGET_FEE_RATIO}% target. You're paying $${totalFees.toFixed(2)} in fees on $${totalGrossProfit.toFixed(2)} gross profit. Consider strategies to reduce trading fees, such as increasing order sizes, using limit orders, or qualifying for volume discounts.`,
      estimatedImpact: {
        amount: monthlySavings,
        period: 'month',
        direction: 'savings',
      },
      confidenceScore: 0.9,
      metadata: {
        source: 'fee-ratio-analyzer',
        currentFeeRatio: feeRatio,
        targetFeeRatio: TARGET_FEE_RATIO,
        totalFees,
        totalGrossProfit,
      },
    });
  }

  return insights;
}

/**
 * Analyze performance trend using moving average
 * Detects whether performance is trending up, down, or flat
 * 
 * @param dailyData - Array of daily profit summaries (should be sorted chronologically)
 * @returns Array of insights about performance trends
 */
export function analyzeTrend(dailyData: DailyProfitSummary[]): ProfitInsight[] {
  if (!dailyData || dailyData.length < 14) {
    return [];
  }

  // Sort by date to ensure chronological order
  const sortedData = [...dailyData].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
  
  // Calculate 7-day moving average
  const dailyProfits = sortedData.map(d => d.net_profit);
  const movingAvg = calculateMovingAverage(dailyProfits, 7);
  
  if (movingAvg.length < 7) {
    return [];
  }

  // Calculate trend slope
  const slope = calculateTrendSlope(movingAvg);
  
  // Convert slope to daily rate of change
  const dailyChange = slope;
  
  const insights: ProfitInsight[] = [];

  // Significant upward trend
  if (dailyChange > 5) { // More than $5/day improvement
    const weeklyGain = dailyChange * 7;
    
    insights.push({
      id: 'trend-pattern-upward',
      type: 'pattern',
      priority: 'medium',
      title: 'Performance Trending Upward',
      description: `Your 7-day moving average shows a positive trend, improving at approximately $${dailyChange.toFixed(2)} per day. This suggests your trading strategy is becoming more effective. Continue your current approach and monitor for sustained improvement.`,
      estimatedImpact: {
        amount: weeklyGain,
        period: 'week',
        direction: 'gain',
      },
      confidenceScore: 0.75,
      metadata: {
        source: 'trend-analyzer',
        slope: slope,
        dailyChange: dailyChange,
        movingAveragePeriod: 7,
      },
    });
  }
  
  // Significant downward trend
  else if (dailyChange < -5) { // More than $5/day decline
    const weeklyRisk = Math.abs(dailyChange) * 7;
    
    insights.push({
      id: 'trend-warning-downward',
      type: 'warning',
      priority: dailyChange < -10 ? 'high' : 'medium',
      title: 'Performance Trending Downward',
      description: `Your 7-day moving average shows a declining trend, decreasing at approximately $${Math.abs(dailyChange).toFixed(2)} per day. This may indicate strategy deterioration or changing market conditions. Review recent trades and consider adjusting your approach.`,
      estimatedImpact: {
        amount: weeklyRisk,
        period: 'week',
        direction: 'risk',
      },
      confidenceScore: 0.75,
      metadata: {
        source: 'trend-analyzer',
        slope: slope,
        dailyChange: dailyChange,
        movingAveragePeriod: 7,
      },
    });
  }

  return insights;
}

/**
 * Analyze consecutive profitable/losing days (streaks)
 * Detects winning streaks and losing streaks
 * 
 * @param dailyData - Array of daily profit summaries (should be sorted chronologically)
 * @returns Array of insights about streaks
 */
export function analyzeConsecutivePerformance(dailyData: DailyProfitSummary[]): ProfitInsight[] {
  if (!dailyData || dailyData.length < 7) {
    return [];
  }

  // Sort by date
  const sortedData = [...dailyData].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
  
  // Find current streak at the end of the period
  let currentStreak = 0;
  let streakType: 'winning' | 'losing' | null = null;
  
  for (let i = sortedData.length - 1; i >= 0; i--) {
    const isProfitable = sortedData[i].net_profit > 0;
    
    if (streakType === null) {
      // First day of streak
      streakType = isProfitable ? 'winning' : 'losing';
      currentStreak = 1;
    } else if ((streakType === 'winning' && isProfitable) || (streakType === 'losing' && !isProfitable)) {
      // Streak continues
      currentStreak++;
    } else {
      // Streak broken
      break;
    }
  }

  const insights: ProfitInsight[] = [];

  // Winning streak (5+ consecutive profitable days)
  if (streakType === 'winning' && currentStreak >= 5) {
    const streakProfit = sortedData.slice(-currentStreak).reduce((sum, d) => sum + d.net_profit, 0);
    const avgDailyProfit = streakProfit / currentStreak;
    
    insights.push({
      id: 'consecutive-pattern-winning',
      type: 'pattern',
      priority: 'medium',
      title: `${currentStreak}-Day Winning Streak`,
      description: `You're currently on a ${currentStreak}-day winning streak with an average daily profit of $${avgDailyProfit.toFixed(2)}. Total profit during this streak: $${streakProfit.toFixed(2)}. Strong momentum indicates your current strategy is working well.`,
      estimatedImpact: {
        amount: avgDailyProfit * 7,
        period: 'week',
        direction: 'gain',
      },
      confidenceScore: 0.7,
      metadata: {
        source: 'consecutive-performance-analyzer',
        streakLength: currentStreak,
        streakType: 'winning',
        avgDailyProfit: avgDailyProfit,
      },
    });
  }
  
  // Losing streak (3+ consecutive losing days)
  else if (streakType === 'losing' && currentStreak >= 3) {
    const streakLoss = Math.abs(sortedData.slice(-currentStreak).reduce((sum, d) => sum + d.net_profit, 0));
    const avgDailyLoss = streakLoss / currentStreak;
    
    insights.push({
      id: 'consecutive-warning-losing',
      type: 'warning',
      priority: currentStreak >= 5 ? 'high' : 'medium',
      title: `${currentStreak}-Day Losing Streak`,
      description: `You're currently experiencing a ${currentStreak}-day losing streak with an average daily loss of $${avgDailyLoss.toFixed(2)}. Total loss during this streak: $${streakLoss.toFixed(2)}. Consider taking a break to reassess your strategy or reduce position sizes until the pattern reverses.`,
      estimatedImpact: {
        amount: avgDailyLoss * 7,
        period: 'week',
        direction: 'risk',
      },
      confidenceScore: 0.8,
      metadata: {
        source: 'consecutive-performance-analyzer',
        streakLength: currentStreak,
        streakType: 'losing',
        avgDailyLoss: avgDailyLoss,
      },
    });
  }

  return insights;
}

/**
 * Analyze win rate trend by comparing current to previous period
 * Uses comparison data if available
 * 
 * @param currentData - Current period daily profit summaries
 * @param previousData - Previous period daily profit summaries (optional)
 * @returns Array of insights about win rate trends
 */
export function analyzeWinRateTrend(
  currentData: DailyProfitSummary[],
  previousData?: DailyProfitSummary[]
): ProfitInsight[] {
  if (!currentData || currentData.length === 0) {
    return [];
  }

  // Calculate current win rate
  const currentProfitableDays = currentData.filter(d => d.net_profit > 0).length;
  const currentWinRate = (currentProfitableDays / currentData.length) * 100;

  const insights: ProfitInsight[] = [];

  // If no previous data, just report current win rate if noteworthy
  if (!previousData || previousData.length === 0) {
    return insights;
  }

  // Calculate previous win rate
  const previousProfitableDays = previousData.filter(d => d.net_profit > 0).length;
  const previousWinRate = (previousProfitableDays / previousData.length) * 100;

  const winRateChange = currentWinRate - previousWinRate;
  const MIN_SIGNIFICANT_CHANGE = 5; // 5 percentage points

  // Improving win rate
  if (winRateChange >= MIN_SIGNIFICANT_CHANGE) {
    insights.push({
      id: 'win-rate-opportunity-improving',
      type: 'opportunity',
      priority: 'medium',
      title: 'Win Rate Improving',
      description: `Your win rate has improved from ${previousWinRate.toFixed(1)}% to ${currentWinRate.toFixed(1)}%, an increase of ${winRateChange.toFixed(1)} percentage points. This positive trend suggests your strategy refinements are working. Maintain your current approach.`,
      estimatedImpact: {
        amount: 0, // Qualitative benefit
        period: 'week',
        direction: 'gain',
      },
      confidenceScore: 0.8,
      metadata: {
        source: 'win-rate-trend-analyzer',
        currentWinRate,
        previousWinRate,
        winRateChange,
      },
    });
  }
  
  // Declining win rate
  else if (winRateChange <= -MIN_SIGNIFICANT_CHANGE) {
    insights.push({
      id: 'win-rate-warning-declining',
      type: 'warning',
      priority: winRateChange <= -10 ? 'high' : 'medium',
      title: 'Win Rate Declining',
      description: `Your win rate has declined from ${previousWinRate.toFixed(1)}% to ${currentWinRate.toFixed(1)}%, a decrease of ${Math.abs(winRateChange).toFixed(1)} percentage points. This trend suggests potential strategy issues or unfavorable market conditions. Review your recent trades and consider adjustments.`,
      estimatedImpact: {
        amount: 0, // Qualitative risk
        period: 'week',
        direction: 'risk',
      },
      confidenceScore: 0.8,
      metadata: {
        source: 'win-rate-trend-analyzer',
        currentWinRate,
        previousWinRate,
        winRateChange,
      },
    });
  }

  return insights;
}
